import * as embedConf from '@anm/constants/embed';
import getPopoverUrl from '@anm/helpers/getPopoverUrl';
import getResponsiveVideoPadding from '@anm/helpers/getResponsiveVideoPadding';
import getThumbnailSrc from '@anm/helpers/getThumbnailSrc';

import vtConfig from '../../../../../config';
import { EmbedData, PopoverType } from '../../../../../store/modules/hostedVideo';

import { GetStatusesProps, HighLightPopoverProps, ThumbnailPopoverProps } from './types';

export const getStatuses = ({
  canPay,
  embedLimit,
  embeddedCount,
  isLimitReached,
  canPurchaseEmbeds
}: GetStatusesProps) => {
  const isNormalCase = !isLimitReached;
  const shouldUpgrade = isLimitReached && !canPurchaseEmbeds;
  const isAllowedPuchaseAboveLimit = isLimitReached && canPurchaseEmbeds;
  const canPurchaseAboveLimit = isAllowedPuchaseAboveLimit && canPay;
  const isPaymentFailed = isAllowedPuchaseAboveLimit && !canPay;
  const isExtraEmbedsUsed = !!(embedLimit && embeddedCount && embeddedCount > embedLimit);

  return {
    isNormalCase,
    shouldUpgrade,
    isPaymentFailed,
    isExtraEmbedsUsed,
    canPurchaseAboveLimit
  };
};

export const formatSnippet = (snippet: string) => snippet.replace(/\s+/g, ' ').replace(/\>[ ]+\</g, '><');

export const generateSnippet = (data: EmbedData | null) => {
  if (!data) return null;

  return data.type === 'popover' ? generatePopoverSnippet(data) : generateInlineSnippet(data);
};

export const generateInlineSnippet = ({ id, sizes, sizeType, isWebinar }: EmbedData) => {
  const ratio = sizes.width / sizes.height;
  const padding = getResponsiveVideoPadding(ratio);
  const responsivePaddingStyles = `style="padding: ${padding} 0 0 0; position: relative"`;
  const responsiveWrapperStyles = `style="height:100%;left:0;position:absolute;top:0;width:100%"`;
  const responsiveIframeStyles = `height="100%" width="100%;"`;
  const fixedIframeStyles = `height="${sizes.height}" width="${sizes.width}"`;
  const defaultIframeAttrs = `frameborder="0" allow="autoplay; fullscreen" scrolling="no"`;
  const iframeSrc = isWebinar
    ? `src="${vtConfig().urls.webinarUrl}${id}?embed"`
    : `src="${vtConfig().urls.embed}${id}"`;

  switch (sizeType) {
    case 'fixed':
      return `<iframe
                ${iframeSrc}
                ${fixedIframeStyles}
                ${defaultIframeAttrs}>
              </iframe>`;

    case 'responsive':
      return `<div ${responsivePaddingStyles}>
                <div ${responsiveWrapperStyles}>
                  <iframe ${responsiveIframeStyles}
                    ${iframeSrc}
                    ${defaultIframeAttrs}>
                  </iframe>
                </div>
              </div>`;
  }
};

export const generatePopoverSnippet = ({
  id,
  sizes,
  sizeType,
  popover: { text, popoverType, button, videoName, thumbSrc }
}: EmbedData) => {
  const isTextPopover = popoverType === 'text';
  const isThumbnailPopover = popoverType === 'thumbnail';
  const isHighLightPopover = popoverType === 'highlight';

  const popoverInnerContent = isTextPopover
    ? getTextPopover(text)
    : isThumbnailPopover
    ? getThumbnailPopover({ sizes, button, sizeType, videoName, videoId: id })
    : isHighLightPopover
    ? getHighLightPopover({ id, videoName, imgSrc: thumbSrc, title: text })
    : null;

  const popoverScript = `<script src="${getPopoverUrl()}"></script>`;

  const containerStyles = isTextPopover ? `style="${getPopoverStyles(popoverType)}"` : '';

  const popoverAttrs = `
    class="${embedConf.POPOVER_SELECTOR}"
    ${embedConf.VIDEO_ID_ATTR}="${id}"
    ${embedConf.VIDEO_WIDTH_ATTR}="${sizes.width}"
    ${embedConf.VIDEO_HEIGHT_ATTR}="${sizes.height}"`;

  return `<div ${popoverAttrs} ${containerStyles}>
            ${popoverInnerContent}
            ${popoverScript}
          </div>`;
};

const getThumbnailPopover = (props: ThumbnailPopoverProps) => {
  const fullPreviewSrc = getThumbnailSrc({
    ...props,
    hostId: props.videoId,
    isProd: vtConfig().isProd,
    responsive: props.sizeType === 'responsive'
  });

  const thumbnailWrapperStyles =
    props.sizeType === 'fixed' ? `style="width: ${props.sizes.width}px; height: ${props.sizes.height}px"` : '';

  return `<div ${thumbnailWrapperStyles}>
            <img
              src="${fullPreviewSrc}"
              alt="${props.videoName}"
              style="${getPopoverStyles('thumbnail')}"
              ${embedConf.POPOVER_ACTION}="${embedConf.POPOVER_TARGET}"
            />
          </div>`;
};

const getTextPopover = (
  text: string
) => `<a href="" ${embedConf.POPOVER_ACTION}="${embedConf.POPOVER_TARGET}">${text}</a>
`;

const getPopoverStyles = (type: PopoverType) =>
  ({
    text: `display: inline;`,
    thumbnail: `cursor: pointer;
                display: block;
                max-width: 100%;
                object-fit: cover;`,
    highlight: ``
  }[type]);

const getHighLightPopover = ({ id, title, imgSrc, videoName }: HighLightPopoverProps) => {
  const contentWrapperStyles = `style="width: 100px; margin: 10px; cursor: pointer;"`;
  const imageWrapperStyles = `style="width: 70px; height: 70px; border-radius: 50%; overflow: hidden; padding: 3px; margin: 0 auto 8px;"`;
  const imageStyles = `style="object-fit: cover; display: block; margin: 0; width: 100%; height: 100%; border-radius: 50%;"`;
  const titleStyles = `style="font-style: normal; font-weight: bold; font-size: 14px; line-height: 17px; text-align: center; color: #292a2b; margin: 0;"`;

  const imgAlt = videoName || title;

  return `<div>
          <style>
            [${embedConf.VIDEO_ID_ATTR}="${id}"] .${embedConf.HIGHLIGHT_WRAPPER_ClASSNAME} {border: 2px solid #292a2b};
            [${embedConf.VIDEO_ID_ATTR}="${id}"].${embedConf.WATCHED_VIDEO_CLASSNAME} .${embedConf.HIGHLIGHT_WRAPPER_ClASSNAME} {border: 2px solid #EBEEF5};
          </style>
            <div ${contentWrapperStyles} ${embedConf.POPOVER_ACTION}="${embedConf.POPOVER_TARGET}">
              <div ${imageWrapperStyles} class="${embedConf.HIGHLIGHT_WRAPPER_ClASSNAME}">
                <img src="${imgSrc}" alt="${imgAlt}" ${imageStyles} />
              </div>
              <p ${titleStyles}>${title}</p>
            </div>
          </div>`;
};
